import { gatewayApi as api } from './gatewayApi';
import { WorkOrder } from './workOrderApi';

// Types
export interface BOM {
  id?: string;
  Name: string;
  ProductId: string;
  ComponentId: string;
  Document?: string;
  Active: boolean;
  
  // Related entities
  Product?: Product;
  Component?: Component;
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Component {
  id: string;
  Name: string;
  Description?: string;
  PartNumber?: string;
  UnitOfMeasure?: string;
  UnitCost?: number;
  Active: boolean;
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Product {
  id: string;
  Name: string;
  Description?: string;
  ProductNumber?: string;
  UnitOfMeasure?: string;
  UnitPrice?: number;
  Active: boolean;
  
  // Work Order linking
  WorkOrders?: WorkOrder[];
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface CreateBOMRequest {
  Name: string;
  ProductId: string;
  ComponentId: string;
  Document?: string;
  Active?: boolean;
}

export interface CreateComponentRequest {
  Name: string;
  Description?: string;
  PartNumber?: string;
  UnitOfMeasure?: string;
  UnitCost?: number;
  Active?: boolean;
}

export interface CreateProductRequest {
  Name: string;
  Description?: string;
  ProductNumber?: string;
  UnitOfMeasure?: string;
  UnitPrice?: number;
  Active?: boolean;
}

export interface SearchBOMsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    Active?: boolean;
    Name?: string;
    ProductId?: string;
    ComponentId?: string;
    [key: string]: any;
  };
}

export interface SearchBOMsResponse {
  data: BOM[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface SearchComponentsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    Active?: boolean;
    Name?: string;
    [key: string]: any;
  };
}

export interface SearchComponentsResponse {
  data: Component[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface SearchProductsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    Active?: boolean;
    Name?: string;
    [key: string]: any;
  };
}

export interface SearchProductsResponse {
  data: Product[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface BOMStructure {
  product: Product;
  components: Array<{
    component: Component;
    bomEntry: BOM;
    quantity?: number;
  }>;
  totalCost: number;
}

// BOM API Service
export const bomApi = {
  // BOM Management
  getAllBOMs: (params?: SearchBOMsRequest): Promise<SearchBOMsResponse> =>
    api.post('/bom/search', params || {}),

  getBOMById: (id: string): Promise<BOM> =>
    api.get(`/bom/${id}`),

  createBOM: (data: CreateBOMRequest): Promise<BOM> =>
    api.post('/bom/', data),

  updateBOM: (data: Partial<BOM>): Promise<BOM> =>
    api.put('/bom/', data),

  deleteBOM: (id: string): Promise<void> =>
    api.delete(`/bom/${id}`),

  searchBOMs: (field: string, value: string): Promise<BOM[]> =>
    api.post('/bom/search', { filter: { [field]: value } }),

  // Component Management
  getAllComponents: (params?: SearchComponentsRequest): Promise<SearchComponentsResponse> =>
    api.post('/component/search', params || {}),

  getComponentById: (id: string): Promise<Component> =>
    api.get(`/component/${id}`),

  createComponent: (data: CreateComponentRequest): Promise<Component> =>
    api.post('/component/', data),

  updateComponent: (data: Partial<Component>): Promise<Component> =>
    api.put('/component/', data),

  deleteComponent: (id: string): Promise<void> =>
    api.delete(`/component/${id}`),

  searchComponents: (field: string, value: string): Promise<Component[]> =>
    api.post('/component/search', { filter: { [field]: value } }),

  // Product Management
  getAllProducts: (params?: SearchProductsRequest): Promise<SearchProductsResponse> =>
    api.post('/product/search', params || {}),

  getProductById: (id: string): Promise<Product> =>
    api.get(`/product/${id}`),

  createProduct: (data: CreateProductRequest): Promise<Product> =>
    api.post('/product/', data),

  updateProduct: (data: Partial<Product>): Promise<Product> =>
    api.put('/product/', data),

  deleteProduct: (id: string): Promise<void> =>
    api.delete(`/product/${id}`),

  searchProducts: (field: string, value: string): Promise<Product[]> =>
    api.post('/product/search', { filter: { [field]: value } }),

  // Work Order Integration
  getWorkOrdersForProduct: (productId: string): Promise<WorkOrder[]> =>
    api.post('/workorder/search', { filter: { ProductId: productId } }),

  linkProductToWorkOrder: (productId: string, workOrderId: string): Promise<void> =>
    api.post('/product/link-workorder', { productId, workOrderId }),

  unlinkProductFromWorkOrder: (productId: string, workOrderId: string): Promise<void> =>
    api.delete(`/product/unlink-workorder/${productId}/${workOrderId}`),

  // BOM Structure Analysis
  getBOMStructure: (productId: string): Promise<BOMStructure> =>
    api.get(`/bom/structure/${productId}`),

  calculateBOMCost: (productId: string): Promise<{ totalCost: number; componentCosts: Array<{ componentId: string; cost: number }> }> =>
    api.get(`/bom/cost/${productId}`),

  // Dropdown Data
  getActiveComponents: (): Promise<Component[]> =>
    api.post('/component/search', { filter: { Active: true }, page: 1, limit: 100 }),

  getActiveProducts: (): Promise<Product[]> =>
    api.post('/product/search', { filter: { Active: true }, page: 1, limit: 100 }),

  getAvailableWorkOrders: (): Promise<WorkOrder[]> =>
    api.post('/workorder/search', { filter: { Active: true }, page: 1, limit: 100 }),

  // Validation utilities
  validateBOM: (bom: Partial<BOM>): string[] => {
    const errors: string[] = [];
    
    if (!bom.Name?.trim()) {
      errors.push('BOM Name is required');
    }
    
    if (!bom.ProductId) {
      errors.push('Product selection is required');
    }
    
    if (!bom.ComponentId) {
      errors.push('Component selection is required');
    }
    
    if (bom.ProductId === bom.ComponentId) {
      errors.push('Product and Component cannot be the same');
    }
    
    return errors;
  },

  validateComponent: (component: Partial<Component>): string[] => {
    const errors: string[] = [];
    
    if (!component.Name?.trim()) {
      errors.push('Component Name is required');
    }
    
    if (component.UnitCost !== undefined && component.UnitCost < 0) {
      errors.push('Unit Cost cannot be negative');
    }
    
    return errors;
  },

  validateProduct: (product: Partial<Product>): string[] => {
    const errors: string[] = [];
    
    if (!product.Name?.trim()) {
      errors.push('Product Name is required');
    }
    
    if (product.UnitPrice !== undefined && product.UnitPrice < 0) {
      errors.push('Unit Price cannot be negative');
    }
    
    return errors;
  },

  // Utility functions
  formatCurrency: (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },

  generateProductNumber: (productName: string): string => {
    // Generate a simple product number based on name
    const prefix = 'PROD';
    const timestamp = Date.now().toString().slice(-6);
    const nameCode = productName.substring(0, 3).toUpperCase();
    return `${prefix}-${nameCode}-${timestamp}`;
  },

  generatePartNumber: (componentName: string): string => {
    // Generate a simple part number based on name
    const prefix = 'COMP';
    const timestamp = Date.now().toString().slice(-6);
    const nameCode = componentName.substring(0, 3).toUpperCase();
    return `${prefix}-${nameCode}-${timestamp}`;
  },

  // BOM Tree structure for hierarchical display
  buildBOMTree: async (productId: string): Promise<{
    product: Product;
    components: Array<{
      bom: BOM;
      component: Component;
      subComponents: BOM[];
    }>;
  }> => {
    try {
      const boms = await bomApi.searchBOMs('ProductId', productId);
      const tree = {
        product: await bomApi.getProductById(productId),
        components: [] as Array<{
          bom: BOM;
          component: Component;
          subComponents: BOM[];
        }>
      };

      for (const bom of boms) {
        const component = await bomApi.getComponentById(bom.ComponentId);
        tree.components.push({
          bom,
          component,
          // Recursively get components if this component is also a product
          subComponents: await bomApi.searchBOMs('ProductId', bom.ComponentId)
        });
      }

      return tree;
    } catch (error) {
      console.error('Failed to build BOM tree:', error);
      throw error;
    }
  },

  // Cost rollup calculations
  calculateTotalProductCost: async (productId: string): Promise<number> => {
    try {
      const boms = await bomApi.searchBOMs('ProductId', productId);
      let totalCost = 0;

      for (const bom of boms) {
        const component = await bomApi.getComponentById(bom.ComponentId);
        totalCost += component.UnitCost || 0;
      }

      return totalCost;
    } catch (error) {
      console.error('Failed to calculate product cost:', error);
      return 0;
    }
  },

  // Export functionality
  exportBOMToCSV: (boms: BOM[]): string => {
    const headers = ['BOM Name', 'Product', 'Component', 'Active', 'Created Date'];
    const rows = boms.map(bom => [
      bom.Name,
      bom.Product?.Name || 'Unknown',
      bom.Component?.Name || 'Unknown',
      bom.Active ? 'Active' : 'Inactive',
      bom.CreatedDate ? new Date(bom.CreatedDate).toLocaleDateString() : 'N/A'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csvContent;
  },

  downloadCSV: (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
}; 